import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { LoadingState } from "../components/LoadingState";
import { browserTimeZone, formatFullDateTime, formatTime, relativeDayLabel, toDateKey } from "../lib/date";
import { supabase } from "../lib/supabase";
import type { DashboardBooking, DashboardNotification } from "../lib/types";

function demoBookings(): DashboardBooking[] {
  const at = (days: number, hour: number) => {
    const value = new Date();
    value.setDate(value.getDate() + days);
    value.setHours(hour, 30, 0, 0);
    return value;
  };
  return [
    {
      id: 1,
      public_id: "demo-1",
      reference: "RSM-7Q2KA9",
      organization_id: 1,
      host_profile_id: 1,
      event_type_id: 1,
      status: "confirmed",
      title: "30-minute working session",
      starts_at: at(0, 10).toISOString(),
      ends_at: at(0, 11).toISOString(),
      guest_name: "Nomsa Dlamini",
      guest_email: "nomsa@example.com",
      guest_timezone: "Africa/Johannesburg",
      guest_notes: "Review the first-pass credit workflow and agree the smallest useful pilot.",
      meeting_url: null,
      location_label: "Online meeting",
      created_at: new Date().toISOString(),
      host_profiles: { display_name: "Shahil Mathapershad", domain_label: "Capital & Credit" },
    },
    {
      id: 2,
      public_id: "demo-2",
      reference: "RSM-40TPV6",
      organization_id: 1,
      host_profile_id: 2,
      event_type_id: 2,
      status: "confirmed",
      title: "30-minute working session",
      starts_at: at(0, 15).toISOString(),
      ends_at: at(0, 16).toISOString(),
      guest_name: "David Chen",
      guest_email: "david@example.com",
      guest_timezone: "Asia/Singapore",
      guest_notes: "Warehouse pick-path constraints and dispatch cut-off times.",
      meeting_url: null,
      location_label: "Online meeting",
      created_at: new Date().toISOString(),
      host_profiles: { display_name: "Rashik Singh", domain_label: "Operations & Supply" },
    },
    {
      id: 3,
      public_id: "demo-3",
      reference: "RSM-1BC9NM",
      organization_id: 1,
      host_profile_id: 1,
      event_type_id: 1,
      status: "confirmed",
      title: "30-minute working session",
      starts_at: at(1, 9).toISOString(),
      ends_at: at(1, 10).toISOString(),
      guest_name: "Aisha Patel",
      guest_email: "aisha@example.com",
      guest_timezone: "Europe/London",
      guest_notes: null,
      meeting_url: null,
      location_label: "Online meeting",
      created_at: new Date().toISOString(),
      host_profiles: { display_name: "Shahil Mathapershad", domain_label: "Capital & Credit" },
    },
  ];
}

const DEMO_NOTIFICATIONS: DashboardNotification[] = [
  { id: 1, booking_id: 1, organization_id: 1, kind: "booking_created", title: "New meeting with Nomsa", body: "Today at 10:30 · Shahil", read_at: null, created_at: new Date().toISOString() },
  { id: 2, booking_id: 2, organization_id: 1, kind: "booking_created", title: "New meeting with David", body: "Today at 15:30 · Rashik", read_at: null, created_at: new Date().toISOString() },
];

export function DashboardPage() {
  const [openedAt] = useState(() => Date.now());
  const [bookings, setBookings] = useState<DashboardBooking[]>([]);
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const client = supabase;
    if (!client) {
      const demo = demoBookings();
      setBookings(demo);
      setNotifications(DEMO_NOTIFICATIONS);
      setSelectedId((current) => {
        const upcoming = demo.filter((booking) => booking.status === "confirmed" && new Date(booking.ends_at).getTime() > Date.now() - 60 * 60_000);
        return upcoming.some((booking) => booking.id === current) ? current : upcoming[0]?.id ?? null;
      });
      setLoading(false);
      return;
    }
    setError(null);
    const [{ data: bookingRows, error: bookingError }, { data: profileRows }, { data: notificationRows, error: notificationError }] = await Promise.all([
      client.from("bookings").select("*").order("starts_at", { ascending: true }).limit(200),
      client.from("host_profiles").select("id, display_name, domain_label"),
      client.from("dashboard_notifications").select("*").order("created_at", { ascending: false }).limit(80),
    ]);
    if (bookingError || notificationError) {
      setError(bookingError?.message ?? notificationError?.message ?? "Dashboard data could not be loaded.");
    } else {
      const profiles = new Map((profileRows ?? []).map((row) => [row.id as number, row]));
      const mapped = (bookingRows ?? []).map((row) => ({
        ...row,
        host_profiles: profiles.get(row.host_profile_id as number) ?? null,
      })) as unknown as DashboardBooking[];
      setBookings(mapped);
      setNotifications((notificationRows ?? []) as unknown as DashboardNotification[]);
      setSelectedId((current) => {
        const upcoming = mapped.filter((booking) => booking.status === "confirmed" && new Date(booking.ends_at).getTime() > Date.now() - 60 * 60_000);
        return upcoming.some((booking) => booking.id === current) ? current : upcoming[0]?.id ?? null;
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    const client = supabase;
    if (!client) return;
    const channel = client
      .channel("dashboard-booking-notifications")
      .on("postgres_changes", { event: "*", schema: "public", table: "dashboard_notifications" }, () => void load())
      .subscribe();
    const refresh = () => { if (document.visibilityState === "visible") void load(); };
    document.addEventListener("visibilitychange", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      void client.removeChannel(channel);
    };
  }, [load]);

  const activeBookings = useMemo(
    () => bookings.filter((booking) => booking.status === "confirmed" && new Date(booking.ends_at).getTime() > openedAt - 60 * 60_000),
    [bookings, openedAt],
  );
  const grouped = useMemo(() => {
    const map = new Map<string, DashboardBooking[]>();
    for (const booking of activeBookings) {
      const key = toDateKey(new Date(booking.starts_at), browserTimeZone);
      map.set(key, [...(map.get(key) ?? []), booking]);
    }
    return [...map.entries()];
  }, [activeBookings]);
  const selected = bookings.find((booking) => booking.id === selectedId) ?? null;
  const unread = notifications.filter((item) => !item.read_at).length;

  const markAllRead = async () => {
    const now = new Date().toISOString();
    setNotifications((items) => items.map((item) => ({ ...item, read_at: item.read_at ?? now })));
    if (supabase) await supabase.from("dashboard_notifications").update({ read_at: now }).is("read_at", null);
  };

  return (
    <main className="agenda-page">
      <header className="dashboard-topbar">
        <div>
          <p className="section-label">Central dashboard</p>
          <h1>Today</h1>
          <p>{activeBookings.length} upcoming {activeBookings.length === 1 ? "meeting" : "meetings"} · {unread} new {unread === 1 ? "update" : "updates"}</p>
        </div>
        <button className="notification-button" type="button" onClick={() => void markAllRead()} aria-label={`${unread} unread booking updates`}>
          <Icon name="notification" />
          {unread ? <span>{unread}</span> : null}
        </button>
      </header>

      {error ? <div className="error-banner" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="Loading meeting desk" /> : (
        <div className="agenda-grid">
          <section className="agenda-list" aria-label="Upcoming meetings">
            {grouped.length ? grouped.map(([date, items]) => (
              <div className="agenda-day" key={date}>
                <div className="agenda-day__heading">
                  <h2>{relativeDayLabel(items[0]!.starts_at, browserTimeZone)}</h2>
                  <span>{date}</span>
                </div>
                {items.map((booking) => (
                  <button className={`agenda-docket${selectedId === booking.id ? " is-selected" : ""}`} type="button" key={booking.id} onClick={() => setSelectedId(booking.id)}>
                    <span className="agenda-docket__time">{formatTime(booking.starts_at, browserTimeZone)}</span>
                    <span className="agenda-docket__body"><strong>{booking.guest_name}</strong><small>{booking.title}</small></span>
                    <span className="agenda-docket__host">{booking.host_profiles?.display_name.split(" ")[0]}</span>
                  </button>
                ))}
              </div>
            )) : (
              <div className="dashboard-empty"><h2>No meetings ahead.</h2><p>New bookings will arrive here automatically.</p></div>
            )}
          </section>

          <aside className="meeting-brief" aria-label="Selected meeting brief">
            {selected ? (
              <>
                <div className="meeting-brief__heading">
                  <p className="section-label">Meeting brief · {selected.reference}</p>
                  <span className={`status status--${selected.status}`}><Icon name="check" /> {selected.status}</span>
                </div>
                <h2>{selected.guest_name}</h2>
                <p className="meeting-brief__title">{selected.title}</p>
                <dl>
                  <div><dt>When</dt><dd>{formatFullDateTime(selected.starts_at, browserTimeZone)}</dd></div>
                  <div><dt>Host</dt><dd>{selected.host_profiles?.display_name ?? "RSM"}<small>{selected.host_profiles?.domain_label}</small></dd></div>
                  <div><dt>Guest timezone</dt><dd>{selected.guest_timezone}</dd></div>
                  <div><dt>Email</dt><dd><a href={`mailto:${selected.guest_email}`}>{selected.guest_email}</a></dd></div>
                  <div><dt>Format</dt><dd>{selected.location_label}</dd></div>
                </dl>
                <section className="brief-notes">
                  <h3>What to prepare</h3>
                  <p>{selected.guest_notes || "No preparation note was added."}</p>
                </section>
                <section className="delivery-status">
                  <h3>Delivery</h3>
                  <p><Icon name="check" /> Added to central dashboard</p>
                  <p><Icon name="check" /> Guest calendar link ready</p>
                </section>
              </>
            ) : <div className="dashboard-empty"><h2>Select a meeting.</h2><p>The full brief will open here.</p></div>}
          </aside>
        </div>
      )}
    </main>
  );
}
